import React, { useState } from 'react';
import { ChevronLeft, X, Eye, EyeOff, Layers, Plus } from 'lucide-react';

export default function AdjustmentLayerPanel({ 
  isOpen, 
  onClose, 
  template_content, 
  settemplate_content,
  currentPageIndex 
}) {
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen) return null;

  const currentPage = template_content?.pages?.[currentPageIndex];
  const items = currentPage?.items || [];
  
  // Get all adjustment layers
  const adjustmentLayers = items.filter(item => item.type === 'adjustment-layer');

  // Create new adjustment layer
  const createAdjustmentLayer = () => {
    const next = { ...template_content };
    const page = next.pages?.[currentPageIndex];
    
    if (!page) return;
    
    // Find the highest layer number
    const maxLayer = Math.max(
      0,
      ...(page.items || []).map(item => item.layer || 0)
    );
    
    const newAdjustmentLayer = {
      id: `adj_layer_${Date.now()}`,
      type: 'adjustment-layer',
      name: `Adjustment Layer ${adjustmentLayers.length + 1}`,
      layer: maxLayer + 1,
      isVisual: true,
      time: {
        start: 0,
        duration: 10 // Default 10 seconds
      },
      effects: [],
      enabled: true
    };
    
    if (!page.items) {
      page.items = [];
    }
    
    page.items.push(newAdjustmentLayer);
    settemplate_content(next);
  };

  // Remove adjustment layer
  const removeAdjustmentLayer = (layerId) => {
    const next = { ...template_content };
    const page = next.pages?.[currentPageIndex];
    
    if (page?.items) {
      page.items = page.items.filter(item => item.id !== layerId);
    }
    
    settemplate_content(next);
  };

  // Toggle adjustment layer enabled state
  const toggleAdjustmentLayer = (layerId) => {
    const next = { ...template_content };
    const page = next.pages?.[currentPageIndex];
    
    if (page?.items) {
      const layer = page.items.find(item => item.id === layerId);
      if (layer) {
        layer.enabled = !layer.enabled;
      }
    }
    
    settemplate_content(next);
  };

  // Update adjustment layer properties
  const updateAdjustmentLayer = (layerId, updates) => {
    const next = { ...template_content };
    const page = next.pages?.[currentPageIndex];
    
    if (page?.items) {
      const layerIndex = page.items.findIndex(item => item.id === layerId);
      if (layerIndex >= 0) {
        page.items[layerIndex] = { ...page.items[layerIndex], ...updates };
      }
    }
    
    settemplate_content(next);
  };

  return (
    <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">Adjustment Layers</h2>
        </div>
        <button
          onClick={createAdjustmentLayer}
          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          title="Create Adjustment Layer"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200">
        <input
          type="text"
          placeholder="Search adjustment layers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {adjustmentLayers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Layers className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-sm">No adjustment layers yet</p>
            <p className="text-xs mt-2">Click the + button to create one</p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {adjustmentLayers
              .filter(layer => 
                searchTerm === '' || 
                layer.name?.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((layer) => (
                <div key={layer.id} className="bg-gray-50 rounded-lg p-3 border-2 border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Layers className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">{layer.name || 'Adjustment Layer'}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => toggleAdjustmentLayer(layer.id)}
                        className={`p-1 rounded ${
                          layer.enabled 
                            ? 'text-green-600 hover:bg-green-100' 
                            : 'text-gray-400 hover:bg-gray-200'
                        }`}
                        title={layer.enabled ? 'Disable' : 'Enable'}
                      >
                        {layer.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => removeAdjustmentLayer(layer.id)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Layer Properties */}
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Name</label>
                      <input
                        type="text"
                        value={layer.name || ''}
                        onChange={(e) => updateAdjustmentLayer(layer.id, { name: e.target.value })}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Start (s)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={layer.time?.start || 0}
                          onChange={(e) => updateAdjustmentLayer(layer.id, { 
                            time: { ...layer.time, start: parseFloat(e.target.value) || 0 }
                          })}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Duration (s)</label>
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={layer.time?.duration || 10}
                          onChange={(e) => updateAdjustmentLayer(layer.id, { 
                            time: { ...layer.time, duration: parseFloat(e.target.value) || 1 }
                          })}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Layer Position</label>
                      <input
                        type="number"
                        min="0"
                        value={layer.layer || 0}
                        onChange={(e) => updateAdjustmentLayer(layer.id, { 
                          layer: parseInt(e.target.value) || 0
                        })}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        title="Higher numbers appear above lower numbers. Affects which items are below this adjustment layer."
                      />
                    </div>
                    
                    <div className="text-xs text-gray-500 mt-2">
                      <p>Effects: {layer.effects?.length || 0}</p>
                      <p className="mt-1">This layer affects all items with layer &lt; {layer.layer || 0}</p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

